import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  Button,
  CheckboxField,
  Field,
  Modal,
  SearchIcon,
  SelectField,
  StatusBadge,
  type Column,
} from "@fawkes/ui";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

export interface LegacyUser {
  id: number;
  username: string;
  loginCapable: boolean;
  status: "MIGRATED" | "MATCHED_BY_NAME" | "NOT_MIGRATED";
  linkedUserId?: string | null;
  linkedUsername?: string | null;
}

interface LegacyUserPage {
  rows: LegacyUser[];
  total: number;
}

export interface LegacyMigrationOutcome {
  legacyUserAccountId: number;
  username?: string | null;
  outcome:
    | "MIGRATED"
    | "RECONCILED"
    | "ALREADY_MIGRATED"
    | "SKIPPED_NO_PASSWORD"
    | "SKIPPED_INACTIVE"
    | "NOT_FOUND"
    | "CONFLICT";
  userId?: string | null;
}

/** Server page size — kept in sync with the api default (LegacyUserMigrationService.DEFAULT_PAGE_SIZE). */
const PAGE_SIZE = 100;

// Migration dialog over /api/users/legacy — the per-account variant of the login-completeness
// backfill. Lists the active docaserver user_account population with its fapi migration status;
// unmigrated, login-capable accounts are multi-selectable and migrate keeping their password
// (the server carries the PBKDF2 hash verbatim). MATCHED_BY_NAME rows are selectable too: the
// server links ("reconciles") the existing user instead of inserting a duplicate.
//
// The legacy population runs to thousands of rows, so the status filter, both name filters and the
// paging are all SERVER-side (`?status=&q=&linkedQ=&page=&size=`, answering `{rows,total}`); the
// order is always login name. The filter defaults to PENDING — the dialog exists to migrate what has
// not been migrated, so the already-done rows are out of the way unless you ask for them.
export function LegacyUserMigrationDialog({
  onClose,
  onMigrated,
}: {
  onClose: () => void;
  onMigrated?: () => void;
}) {
  const t = useTranslate();
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const [status, setStatus] = useState("PENDING");
  const [query, setQuery] = useState("");
  const [linkedQuery, setLinkedQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [debouncedLinkedQuery, setDebouncedLinkedQuery] = useState("");
  const [page, setPage] = useState(1);

  // Debounce both name filters into the server query; a new search resets to the first page so the
  // user never lands on an out-of-range page.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setDebouncedLinkedQuery(linkedQuery.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [query, linkedQuery]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedQuery) params.set("q", debouncedQuery);
    if (debouncedLinkedQuery) params.set("linkedQ", debouncedLinkedQuery);
    params.set("status", status);
    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));
    return `users/legacy?${params.toString()}`;
  }, [debouncedQuery, debouncedLinkedQuery, status, page]);

  const { data, isLoading, isError, refetch } = useCustom<LegacyUserPage>({ url, method: "get" });
  const rows = useMemo(() => data?.data?.rows ?? [], [data]);
  const total = data?.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // Only what is on screen can be bulk-toggled — "select all" over a filtered, paged set would
  // otherwise silently queue thousands of migrations the operator never saw.
  const selectableOnPage = useMemo(
    () => rows.filter((u) => u.status !== "MIGRATED" && u.loginCapable),
    [rows],
  );
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((u) => selected.has(u.id));

  const { mutate: migrate, isLoading: migrating } = useCustomMutation<LegacyMigrationOutcome[]>();

  const toggle = (id: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // Selection is keyed by id and deliberately survives paging and filtering, so an operator can
  // gather accounts across several pages and migrate them in one go.
  const toggleAllOnPage = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const u of selectableOnPage) {
        if (checked) next.add(u.id);
        else next.delete(u.id);
      }
      return next;
    });
  };

  const setStatusFilter = (next: string) => {
    setStatus(next);
    setPage(1);
  };

  const submit = () => {
    setNotice(null);
    migrate(
      {
        url: "users/legacy/migrate",
        method: "post",
        values: { legacyUserAccountIds: [...selected] },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (result) => {
          const outcomes = (result.data ?? []) as LegacyMigrationOutcome[];
          const ok = outcomes.filter(
            (o) => o.outcome === "MIGRATED" || o.outcome === "RECONCILED",
          ).length;
          const skipped = outcomes.length - ok;
          setNotice({
            kind: skipped > 0 ? "error" : "success",
            text:
              skipped > 0
                ? t("legacyMigrationDialog.result.partial", { args: [ok, skipped] })
                : t("legacyMigrationDialog.result.success", { args: [ok] }),
          });
          setSelected(new Set());
          refetch();
          onMigrated?.();
        },
        onError: () => setNotice({ kind: "error", text: t("legacyMigrationDialog.failed") }),
      },
    );
  };

  const statusBadge = (u: LegacyUser) => {
    if (u.status === "MIGRATED") {
      return <StatusBadge tone="success">{t("legacyMigrationDialog.status.migrated")}</StatusBadge>;
    }
    if (u.status === "MATCHED_BY_NAME") {
      return (
        <StatusBadge tone="warning">{t("legacyMigrationDialog.status.matchedByName")}</StatusBadge>
      );
    }
    if (!u.loginCapable) {
      return <StatusBadge tone="neutral">{t("legacyMigrationDialog.status.noPassword")}</StatusBadge>;
    }
    return <StatusBadge tone="neutral">{t("legacyMigrationDialog.status.notMigrated")}</StatusBadge>;
  };

  const columns: Column<LegacyUser>[] = [
    {
      header: "",
      cell: (u) => (
        <input
          type="checkbox"
          aria-label={t("legacyMigrationDialog.selectRow", { args: [u.username] })}
          disabled={u.status === "MIGRATED" || !u.loginCapable || migrating}
          checked={selected.has(u.id)}
          onChange={(e) => toggle(u.id, e.target.checked)}
        />
      ),
    },
    { header: t("userListView.column.username"), cell: (u) => u.username },
    { header: t("userListView.column.status"), cell: statusBadge },
    {
      header: t("legacyMigrationDialog.column.linkedUser"),
      cell: (u) => u.linkedUsername || "—",
    },
  ];

  return (
    <Modal
      open
      // Wide: the filter row + the 4-column grid need more than the form-width default.
      size="wide"
      title={t("legacyMigrationDialog.title")}
      onClose={onClose}
      footer={
        <>
          {notice && (
            <span
              className={notice.kind === "error" ? "fawkes-error" : "fawkes-muted"}
              role="status"
            >
              {notice.text}
            </span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={migrating}>
            {t("common.close")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={migrating || selected.size === 0}>
            {t("legacyMigrationDialog.migrateButton", { args: [selected.size] })}
          </Button>
        </>
      }
    >
      <p className="fawkes-muted">{t("legacyMigrationDialog.intro")}</p>

      <div className="legacy-migration__filters">
        <SelectField
          label={t("legacyMigrationDialog.filter.status")}
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: "PENDING", label: t("legacyMigrationDialog.filter.status.pending") },
            { value: "MIGRATED", label: t("legacyMigrationDialog.filter.status.migrated") },
            { value: "ALL", label: t("legacyMigrationDialog.filter.status.all") },
          ]}
        />
        {/* Field, not the ad-hoc .fawkes-products-search markup the list screens use: it shares the
            .fui-field vocabulary with SelectField above, so the three controls match in height,
            font and focus ring by construction rather than by hand-tuned CSS. */}
        <Field
          label={t("legacyMigrationDialog.filter.username")}
          icon={<SearchIcon width={16} height={16} />}
          type="search"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Field
          label={t("legacyMigrationDialog.filter.linkedUser")}
          icon={<SearchIcon width={16} height={16} />}
          type="search"
          autoComplete="off"
          value={linkedQuery}
          onChange={(e) => setLinkedQuery(e.target.value)}
        />
      </div>

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error" role="alert">
          {t("legacyMigrationDialog.loadFailed")}
        </p>
      ) : (
        <>
          <CheckboxField
            label={t("legacyMigrationDialog.selectAllOnPage")}
            checked={allOnPageSelected}
            disabled={selectableOnPage.length === 0 || migrating}
            onChange={(e) => toggleAllOnPage(e.target.checked)}
          />
          <IndeelbareTabel tabelKey="legacy-user-migration-dialog"
            columns={columns}
            rows={rows}
            getRowKey={(u) => String(u.id)}
            empty={t("legacyMigrationDialog.empty")}
          />
          {total > 0 && (
            <div className="fawkes-relatie-list__pager">
              <span className="fawkes-relatie-list__pager-range">
                {t("userListView.pagination.range", { args: [from, to, total] })}
              </span>
              <div className="fawkes-relatie-list__pager-controls">
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || migrating}
                >
                  {t("userListView.pagination.previous")}
                </Button>
                <span className="fawkes-relatie-list__pager-page">
                  {t("userListView.pagination.page", { args: [page, pageCount] })}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page >= pageCount || migrating}
                >
                  {t("userListView.pagination.next")}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
